import { apiClient } from './apiClient';
import type { OpportunitySignal } from '../types/opportunitySignal';

/** Derived from lead score on the server; mirrors `crm/scoring.py`. */
export type LeadTemperature = 'hot' | 'warm' | 'cold';
export type LeadLifecycleStatus = 'active' | 'dormant' | 'archived' | 'do_not_contact';
export type ProspectLifecycleStatus = 'active' | 'nurturing' | 'dormant' | 'lost';
export type ClientLifecycleStatus = 'active' | 'paused' | 'former';
export type PipelineClientLifecycleStatus =
  | LeadLifecycleStatus
  | ProspectLifecycleStatus
  | ClientLifecycleStatus;

export interface Client {
  id: string;
  title?: string;
  full_name: string;
  email: string;
  phone: string;
  /** Heuristic 0–100 from API when present; else computed client-side. */
  lead_score?: number;
  /** Stored opportunity score from signal engine when present. */
  score?: number | null;
  opportunity_signals?: OpportunitySignal[];
  /** User-facing priority band from score. */
  lead_temperature?: LeadTemperature;
  /** Expected base deal value for pipeline forecasting (pre-sale). */
  estimated_deal_value?: string | null;
  /** Sales workflow for lead-stage pipeline contacts (mirrors crm.Lead.status). */
  lead_workflow_status?: string | null;
  touches?: number;
  website_url?: string;
  pain_points?: string;
  objections?: string;
  monthly_value?: string | null;
  demo_at?: string | null;
  last_contacted?: string | null;
  next_follow_up_at?: string | null;
  stage: 'lead' | 'prospect' | 'client' | 'churned' | 'lost' | 'spam';
  /** Why the deal was lost (required when stage='lost'). */
  lost_reason?: string | null;
  /** Page URL or origin descriptor for the capture. */
  source_detail?: string | null;
  stage_changed_at?: string | null;
  // Essentials qualification fields (all optional)
  service_requested?: string | null;
  property_type?: string | null;
  estimated_size?: number | null;
  service_location?: string | null;
  desired_start_date?: string | null;
  preferred_contact_method?: string | null;
  lifecycle_status?: PipelineClientLifecycleStatus;
  lifecycle_status_updated_at?: string | null;
  /** How the record was created (manual, csv_import, website, quick_capture). */
  capture_type?: string;
  captured_at?: string | null;
  captured_location?: string;
  requires_review?: boolean;
  /** Signed API path to image; use authenticated fetch — see AuthenticatedCaptureImage. */
  capture_image_url?: string | null;
  /** When true, outbound email sends are blocked (suppression). */
  do_not_contact?: boolean;
  /** Set when opted out or marked Do Not Contact. */
  unsubscribed_at?: string | null;
  /** How DNC was set (detail API); e.g. email opt-out vs team. */
  unsubscribe_source?: string | null;
  source: string;
  company_name: string;
  job_title: string;
  tags: string[];
  assigned_to: string | null;
  /** Present on list responses (`ClientListSerializer`). */
  assigned_to_name?: string | null;
  is_active: boolean;
  notes?: string;
  address?: string;
  custom_fields: Record<string, any>;
  display_name: string;
  notes_count: number;
  tasks_count: number;
  documents_count: number;
  created_at: string;
  updated_at: string;
}

export interface ClientNote {
  id: string;
  title: string;
  content: string;
  is_pinned: boolean;
  is_private: boolean;
  author: string;
  author_name: string;
  created_at: string;
  updated_at: string;
}

export interface CrmActivityItem {
  id: string;
  action: string;
  description: string;
  metadata?: Record<string, any>;
  user_name?: string;
  created_at: string;
}

export interface TenantUserOption {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  due_date: string | null;
  completed_at: string | null;
  assigned_to: string;
  assigned_to_name: string;
  created_by: string;
  created_by_name: string;
  client: string | null;
  client_name: string | null;
  created_at: string;
  updated_at: string;
}

/** Workflow status values for `crm.Lead` / `Client.lead_workflow_status` (backend Lead.STATUS_CHOICES). */
export type CrmLeadWorkflowStatus =
  | 'new'
  | 'attempted'
  | 'contacted'
  | 'replied'
  | 'qualified'
  | 'unqualified';

/** Maps legacy API strings to canonical SMB workflow values. */
const LEGACY_LEAD_STATUS_TO_WORKFLOW: Record<string, CrmLeadWorkflowStatus> = {
  new_lead: 'new',
  proposal_sent: 'contacted',
  responded: 'replied',
  demo_scheduled: 'attempted',
  demo_completed: 'attempted',
  closed_won: 'qualified',
  closed_lost: 'unqualified',
  converted: 'qualified',
  lost: 'unqualified',
};

const CANONICAL_LEAD_STATUSES = new Set<CrmLeadWorkflowStatus>([
  'new',
  'attempted',
  'contacted',
  'replied',
  'qualified',
  'unqualified',
]);

export function normalizeCrmLeadStatus(status: string | null | undefined): CrmLeadWorkflowStatus {
  const s = (status ?? '').trim().toLowerCase();
  if (!s) return 'new';
  if (LEGACY_LEAD_STATUS_TO_WORKFLOW[s]) return LEGACY_LEAD_STATUS_TO_WORKFLOW[s];
  if (CANONICAL_LEAD_STATUSES.has(s as CrmLeadWorkflowStatus)) return s as CrmLeadWorkflowStatus;
  return 'new';
}

/** `datetime-local` value from ISO; empty if unset/invalid. */
export function isoToDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function datetimeLocalToIso(value: string): string | null {
  if (!value?.trim()) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function withLocalTimezoneParams<T extends Record<string, any> | undefined>(params?: T): T & {
  tz?: string;
  tz_offset_min?: string;
} {
  const enriched: Record<string, any> = { ...(params || {}) };
  if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) enriched.tz = tz;
  }
  enriched.tz_offset_min = String(new Date().getTimezoneOffset());
  return enriched as T & { tz?: string; tz_offset_min?: string };
}

/** Marketing/form lead (`crm.Lead`), distinct from pipeline Client with stage=lead */
export interface CrmLead {
  id: string;
  title?: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone: string;
  company: string;
  job_title: string;
  source: string;
  source_details: string;
  /** Broad category: website, import, campaign, etc. */
  source_type?: string;
  /** UTM and capture metadata */
  attribution?: Record<string, string>;
  status: CrmLeadWorkflowStatus | string;
  lifecycle_status?: LeadLifecycleStatus;
  lifecycle_status_updated_at?: string | null;
  message: string;
  score: number;
  opportunity_signals?: OpportunitySignal[];
  lead_temperature?: LeadTemperature | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  revenue_amount?: string | null;
  last_contacted: string | null;
  next_follow_up_at: string | null;
  touches?: number;
  converted_to_client: string | null;
  /** Always derived from `score` on the server (hot/warm/cold). */
  temperature_from_score?: LeadTemperature;
  notes?: string;
  address?: string;
  created_at: string;
  updated_at: string;
  do_not_contact?: boolean;
  unsubscribed_at?: string | null;
  unsubscribe_source?: string | null;
  images?: LeadImage[];
}

export interface LeadImage {
  id: string;
  lead: string;
  caption: string;
  image_type: string;
  uploaded_by: string | null;
  uploaded_at: string;
  ai_analysis: Record<string, any>;
  extracted_text: string;
  image_url: string | null;
}

export interface ClientStats {
  total_clients: number;
  leads: number;
  prospects: number;
  clients: number;
  churned: number;
  active_clients: number;
  recent_clients: number;
  clients_by_source: Record<string, number>;
  clients_by_stage: Record<string, number>;
  /** Pipeline `Client` rows in lead stage, by derived temperature. */
  leads_hot?: number;
  leads_warm?: number;
  leads_cold?: number;
  /** Inbound marketing leads (`crm.Lead`), all statuses. */
  marketing_leads_total?: number;
  marketing_leads_new?: number;
}

/** Sales dashboard conversion automation (backend `conversion_rules`). */
export interface SalesDashboardAttentionItem {
  id: string;
  severity: 'high' | 'medium' | 'low';
  title: string;
  detail: string;
  href: string;
  kind?: string;
  count?: number;
}

export interface WorkflowSuggestion {
  id: string;
  title: string;
  detail: string;
  href: string;
  severity?: string;
}

export type TodayFeedKind = 'overdue' | 'due_today' | 'new_inbound' | 'needs_review';

export interface TodayFeedItem {
  kind: TodayFeedKind;
  lead_id: string | null;
  client_id: string | null;
  name: string;
  owner: string;
  reason: string;
  urgency_rank: number;
  temperature: LeadTemperature | null;
  href: string;
}

export interface TodayCounts {
  overdue: number;
  due_today: number;
  needs_review: number;
  new: number;
  total: number;
}

export interface DashboardKpis {
  leads_in_pipeline: number;
  followups_due_today: number;
  visitors_30d: number;
  prospects: number;
  clients: number;
  revenue_booked: number;
  revenue_collected_30d: number;
}

export interface DashboardSectionList<T = Client> {
  has_data: boolean;
  items?: T[];
}

export interface DashboardSections {
  hot_leads: DashboardSectionList;
  recently_contacted: DashboardSectionList;
  demos_this_week: DashboardSectionList;
  pipeline_funnel: {
    has_data: boolean;
    leads: number;
    prospects: number;
    clients: number;
    all_zero_after_leads: boolean;
  };
  attention: {
    has_data: boolean;
    items: SalesDashboardAttentionItem[];
  };
  revenue_by_source: {
    has_data: boolean;
    items: Array<{ source: string; revenue: string }>;
  };
  website_traffic: {
    has_data: boolean;
    visitors_30d: number;
    tracking_live?: boolean;
  };
  lead_sources?: LeadSourceBreakdown;
}

export interface LeadSourceBreakdownItem {
  key: string;
  label: string;
  count: number;
  percent: number;
}

export interface LeadSourceBreakdown {
  has_data: boolean;
  total: number;
  items: LeadSourceBreakdownItem[];
  /** 30 for the rolling window; null when it fell back to all-time. */
  window_days: number | null;
}

export interface ContactFollowUpReminder {
  id: string;
  tenant: string;
  user: string;
  client: string | null;
  lead: string | null;
  remind_at: string;
  status: 'pending' | 'completed' | 'dismissed';
  note: string;
  contact_label?: string;
  created_at: string;
  updated_at: string;
}

class CRMService {

  // Client methods
  async getClients(params?: {
    search?: string;
    stage?: string;
    source?: string;
    assigned_to?: string;
    ordering?: string;
    /** Hot/warm/cold first, then soonest next follow-up (backend `sort=attention`). */
    sort?: 'attention';
    follow_up?: 'today' | 'overdue';
    temperature?: LeadTemperature;
    workflow_status?: CrmLeadWorkflowStatus;
    lifecycle_status?: PipelineClientLifecycleStatus | 'all';
    closed_won?: '1';
    demo_week?: '1';
    page?: string;
    /** Compliance: all (default) | contactable (not DNC) | do_not_contact */
    contact_filter?: 'all' | 'contactable' | 'do_not_contact';
  }): Promise<{ count: number; results: Client[]; next: string | null; previous: string | null }> {
    return apiClient.get('/clients/', withLocalTimezoneParams(params));
  }

  /** Fetches all pages of clients (default API page size is 20). */
  async getAllClients(params?: {
    search?: string;
    stage?: string;
    assigned_to?: string;
    ordering?: string;
  }): Promise<Client[]> {
    const all: Client[] = [];
    let page = 1;
    while (true) {
      const res = await this.getClients({ ...params, page: String(page) });
      all.push(...(res.results || []));
      if (!res.next) break;
      page += 1;
    }
    return all;
  }

  async getClient(id: string): Promise<Client> {
    return apiClient.get(`/clients/${id}/`);
  }

  async createClient(clientData: Partial<Client>): Promise<Client> {
    return apiClient.post('/clients/', clientData);
  }

  async updateClient(id: string, clientData: Partial<Client>): Promise<Client> {
    return apiClient.patch(`/clients/${id}/`, clientData);
  }

  async logClientTouch(
    id: string,
    data: { channel: 'call' | 'email' | 'text'; note?: string }
  ): Promise<Client> {
    return apiClient.post(`/clients/${id}/log-touch/`, data);
  }

  async markClientFollowedUp(id: string): Promise<Client> {
    return apiClient.post(`/clients/${id}/mark-followed-up/`, {});
  }

  async deleteClient(id: string): Promise<void> {
    return apiClient.delete(`/clients/${id}/`);
  }

  /** Marketing/form leads (`GET /leads/`) */
  async getMarketingLead(id: string): Promise<CrmLead> {
    return apiClient.get(`/leads/${id}/`);
  }

  async getMarketingLeads(params?: {
    search?: string;
    status?: string;
    lifecycle_status?: LeadLifecycleStatus | 'all';
    source?: string;
    assigned_to?: string;
    ordering?: string;
    page?: string;
    temperature?: LeadTemperature;
    follow_up?: 'today' | 'overdue';
    contact_filter?: 'all' | 'contactable' | 'do_not_contact';
  }): Promise<{ count: number; results: CrmLead[]; next: string | null; previous: string | null }> {
    return apiClient.get('/leads/', withLocalTimezoneParams(params));
  }

  async patchMarketingLead(
    id: string,
    data: Partial<{
      status: CrmLeadWorkflowStatus | string;
      score: number;
      assigned_to: string | null;
      last_contacted: string | null;
      next_follow_up_at: string | null;
      message: string;
      lead_temperature: LeadTemperature | null;
      lifecycle_status: LeadLifecycleStatus;
      notes: string;
      address: string;
      do_not_contact?: boolean;
    }>
  ): Promise<CrmLead> {
    return apiClient.patch(`/leads/${id}/`, data);
  }

  /** Convert marketing lead to pipeline `Client` (prospect stage). */
  async convertMarketingLead(id: string): Promise<{ message: string; client_id: string; lead_id: string }> {
    return apiClient.post(`/leads/${id}/convert/`, {});
  }

  // Client notes methods
  async getClientNotes(clientId: string): Promise<ClientNote[]> {
    return apiClient.get(`/clients/${clientId}/notes/`);
  }

  async createClientNote(clientId: string, noteData: {
    title: string;
    content: string;
    is_pinned?: boolean;
    is_private?: boolean;
  }): Promise<ClientNote> {
    return apiClient.post(`/clients/${clientId}/notes/`, noteData);
  }

  async getClientActivity(clientId: string): Promise<CrmActivityItem[]> {
    return apiClient.get(`/clients/${clientId}/activity/`);
  }

  async getTenantUsers(): Promise<TenantUserOption[]> {
    const res = await apiClient.get('/users/');
    // /users/ is paginated for some tenants; normalize to an array.
    return Array.isArray(res) ? res : res?.results ?? [];
  }

  // Client tasks methods
  async getClientTasks(clientId: string): Promise<Task[]> {
    return apiClient.get(`/clients/${clientId}/tasks/`);
  }

  async createClientTask(clientId: string, taskData: {
    title: string;
    description?: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    due_date?: string;
    assigned_to?: string;
  }): Promise<Task> {
    return apiClient.post(`/clients/${clientId}/tasks/`, taskData);
  }

  // Task methods
  async getTasks(params?: {
    search?: string;
    status?: string;
    priority?: string;
    /** A user id, or the literal 'me'. */
    assigned_to?: string;
    client?: string;
    due?: 'overdue' | 'today' | 'week';
    /** '1' to exclude completed and cancelled. */
    open?: string;
    ordering?: string;
    page?: number;
  }): Promise<{ count: number; results: Task[] }> {
    return apiClient.get('/tasks/', params);
  }

  async createTask(taskData: {
    title: string;
    description?: string;
    assigned_to: string;
    client?: string | null;
    priority?: Task['priority'];
    status?: Task['status'];
    due_date?: string | null;
  }): Promise<Task> {
    return apiClient.post('/tasks/', taskData);
  }

  /** PATCH so partial edits (e.g. status only) don't require the whole task. */
  async updateTask(id: string, taskData: Partial<Task>): Promise<Task> {
    return apiClient.patch(`/tasks/${id}/`, taskData);
  }

  async deleteTask(id: string): Promise<void> {
    return apiClient.delete(`/tasks/${id}/`);
  }

  // Statistics methods
  async getClientStats(): Promise<ClientStats> {
    return apiClient.get('/stats/');
  }

  async getSalesDashboard(): Promise<{
    follow_ups_today: Client[];
    overdue_leads: Client[];
    hot_leads: Client[];
    demos_this_week: Client[];
    recently_contacted: Client[];
    needs_review_leads?: Client[];
    attention_items?: SalesDashboardAttentionItem[];
    workflow_suggestions?: WorkflowSuggestion[];
    today: TodayFeedItem[];
    today_counts: TodayCounts;
    kpis: DashboardKpis;
    sections: DashboardSections;
  }> {
    return apiClient.get('/sales-dashboard/', withLocalTimezoneParams({}));
  }

  /** Quick Capture: photo -> lead-stage pipeline `Client` (with capture image). */
  async quickCaptureLead(formData: FormData): Promise<Client> {
    return apiClient.postFormData('/leads/quick-capture/', formData);
  }

  async listContactReminders(params?: {
    status?: string;
    client_id?: string;
    lead_id?: string;
    page?: string;
  }): Promise<{ count: number; results: ContactFollowUpReminder[]; next: string | null; previous: string | null }> {
    return apiClient.get('/contact-reminders/', params);
  }

  async createContactReminder(payload: {
    client_id?: string;
    lead_id?: string;
    remind_at: string;
    note?: string;
  }): Promise<ContactFollowUpReminder> {
    return apiClient.post('/contact-reminders/', payload);
  }

  async patchContactReminder(
    id: string,
    payload: Partial<Pick<ContactFollowUpReminder, 'status' | 'remind_at' | 'note'>>
  ): Promise<ContactFollowUpReminder> {
    return apiClient.patch(`/contact-reminders/${id}/`, payload);
  }

  async csvImportPreview(formData: FormData): Promise<{
    job_id: string;
    headers: string[];
    row_count: number;
    sample_rows: Record<string, string>[];
    suggested_mapping: Record<string, string>;
    field_defs: Record<string, { label: string; required: boolean }>;
    object_type: string;
  }> {
    return apiClient.postFormData('/imports/csv/preview/', formData);
  }

  async csvImportExecute(payload: {
    job_id: string;
    column_mapping: Record<string, string>;
  }): Promise<{
    job_id: string;
    object_type: string;
    total_rows: number;
    imported_rows: number;
    skipped_rows: number;
    failed_rows: number;
    failure_reason_counts: Record<string, number>;
    failures_sample: Array<{ row_index: number; errors: string[]; sample: Record<string, string> }>;
    failed_rows_csv: string;
  }> {
    return apiClient.post('/imports/csv/execute/', payload);
  }

  async getCsvImportJobs(): Promise<{
    results: Array<{
      id: string;
      object_type: string;
      original_filename: string;
      status: string;
      total_rows: number;
      imported_rows: number;
      skipped_rows: number;
      failed_rows: number;
      created_at: string;
    }>;
  }> {
    return apiClient.get('/imports/');
  }

  async generateOutreachMessage(payload: { business_name: string; notes?: string }): Promise<{ message: string }> {
    return apiClient.post('/ai/outreach/', payload);
  }

  async getRecentActivity(): Promise<any[]> {
    return apiClient.get('/activity/');
  }
}

export const crmService = new CRMService();
